import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { certificateAPI } from '../../services/api';
import './VerifyCertificatePage.css';

const TYPE_LABELS = {
  birth: 'जन्म प्रमाणपत्र', death: 'मृत्यू प्रमाणपत्र', marriage: 'विवाह प्रमाणपत्र',
  residence: 'रहिवासी प्रमाणपत्र', income: 'उत्पन्न प्रमाणपत्र', caste: 'जात प्रमाणपत्र',
  domicile: 'अधिवास प्रमाणपत्र', no_objection: 'ना हरकत प्रमाणपत्र', land: 'जमीन प्रमाणपत्र',
  character: 'चारित्र्य प्रमाणपत्र', migration: 'स्थलांतर प्रमाणपत्र', bpl: 'दारिद्र्यरेषेखालील दाखला',
  disability: 'दिव्यांग नोंद दाखला', senior_citizen: 'ज्येष्ठ नागरिक दाखला', other: 'इतर प्रमाणपत्र',
};

export default function VerifyCertificatePage() {
  const { tenantSlug, certificateNumber } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Manual search
  const [searchNum, setSearchNum] = useState(certificateNumber || '');

  useEffect(() => {
    if (certificateNumber) verify(certificateNumber);
    else setLoading(false);
  }, [certificateNumber]); // eslint-disable-line

  const verify = async (certNum) => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await certificateAPI.verify(tenantSlug, certNum);
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('हे प्रमाणपत्र क्रमांक सापडले नाही. कृपया क्रमांक तपासा.');
      } else {
        setError('सत्यापन करण्यात अयशस्वी. कृपया पुन्हा प्रयत्न करा.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchNum.trim()) verify(searchNum.trim());
  };

  return (
    <div className="verify-certificate-page" style={{ maxWidth: 700, margin: '30px auto', padding: '0 16px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 30 }}>
        <div style={{ fontSize: '2rem', marginBottom: 8 }}>🔍</div>
        <h1 style={{ fontSize: '1.5rem', color: '#1a237e', margin: '0 0 6px' }}>प्रमाणपत्र सत्यापन</h1>
        <p style={{ color: '#666', fontSize: '0.9rem', margin: 0 }}>Certificate Verification</p>
      </div>

      {/* Search form */}
      <form className="verify-search-form" onSubmit={handleSearch} style={{ display: 'flex', gap: 10, marginBottom: 30 }}>
        <input
          type="text"
          value={searchNum}
          onChange={e => setSearchNum(e.target.value)}
          placeholder="प्रमाणपत्र क्रमांक टाका (e.g. CERT-1-000001)"
          style={{ flex: 1, padding: '12px 16px', borderRadius: 8, border: '2px solid #ddd', fontSize: '0.95rem', fontFamily: 'monospace' }}
        />
        <button type="submit" style={{ background: '#1a237e', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 8, fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          सत्यापित करा
        </button>
      </form>

      {loading && (
        <div style={{ textAlign: 'center', padding: 30 }}>
          <div style={{ fontSize: '1.5rem', marginBottom: 10 }}>⏳</div>
          <p style={{ color: '#666' }}>सत्यापन होत आहे...</p>
        </div>
      )}

      {error && (
        <div style={{ background: '#ffebee', borderRadius: 12, padding: '20px 24px', textAlign: 'center', border: '1px solid #ffcdd2' }}>
          <div style={{ fontSize: '2rem', marginBottom: 8 }}>❌</div>
          <p style={{ color: '#c62828', fontSize: '1rem', fontWeight: 600, margin: '0 0 8px' }}>अवैध प्रमाणपत्र</p>
          <p style={{ color: '#666', fontSize: '0.88rem', margin: 0 }}>{error}</p>
        </div>
      )}

      {result && (
        <div className="verify-result-card" style={{ borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          {/* Validity banner */}
          <div style={{
            background: result.valid
              ? 'linear-gradient(135deg, #2e7d32, #43a047)'
              : 'linear-gradient(135deg, #c62828, #e53935)',
            padding: '16px 24px', textAlign: 'center', color: '#fff',
          }}>
            <div style={{ fontSize: '1.8rem', marginBottom: 4 }}>{result.valid ? '✅' : '⚠️'}</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>
              {result.valid ? 'प्रमाणपत्र वैध आहे' : 'प्रमाणपत्र अद्याप जारी नाही'}
            </div>
            <div style={{ fontSize: '0.82rem', opacity: 0.9, marginTop: 2 }}>
              {result.valid ? 'This certificate is valid and verified' : `Current status: ${result.certificate?.status?.replace(/_/g, ' ')}`}
            </div>
          </div>

          {/* Certificate details */}
          <div style={{ background: '#fff', padding: '20px 24px' }}>
            {/* GP Info */}
            {result.grampanchayat && (
              <div style={{ textAlign: 'center', marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid #eee' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a237e' }}>{result.grampanchayat.name}</div>
                <div style={{ fontSize: '0.82rem', color: '#666' }}>
                  {[result.grampanchayat.taluka && `ता. ${result.grampanchayat.taluka}`, result.grampanchayat.district && `जि. ${result.grampanchayat.district}`, result.grampanchayat.state].filter(Boolean).join(', ')}
                </div>
              </div>
            )}

            {/* Certificate meta */}
            <div className="verify-details-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px', marginBottom: 20 }}>
              <div style={{ background: '#f8f9ff', borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>प्रमाणपत्र क्र.</div>
                <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1a237e', fontFamily: 'monospace' }}>{result.certificate.certificate_number}</div>
              </div>
              <div style={{ background: '#f8f9ff', borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>प्रकार</div>
                <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#333' }}>{TYPE_LABELS[result.certificate.certificate_type] || result.certificate.certificate_type}</div>
              </div>
              <div style={{ background: '#f8f9ff', borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>अर्जदार</div>
                <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#333' }}>{result.certificate.subject_name || '—'}</div>
              </div>
              <div style={{ background: '#f8f9ff', borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>जारी तारीख</div>
                <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#333' }}>
                  {result.certificate.issued_at
                    ? new Date(result.certificate.issued_at).toLocaleDateString('mr-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                    : '—'}
                </div>
              </div>
            </div>

            <div style={{ paddingTop: 4, borderTop: '1px solid #eee' }}>
              <div style={{ fontSize: '0.78rem', color: '#1a237e', fontWeight: 700, textTransform: 'uppercase', margin: '14px 0 8px' }}>दस्तऐवज अखंडता / Document integrity</div>
              <div className="verify-details-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: '0.82rem' }}>
                <div style={{ padding: '8px 10px', background: '#fafafa', borderRadius: 6 }}><span style={{ color: '#888' }}>Template version: </span><strong>{result.certificate.template_version || '—'}</strong></div>
                <div style={{ padding: '8px 10px', background: '#fafafa', borderRadius: 6 }}><span style={{ color: '#888' }}>Revision: </span><strong>{result.certificate.revision || '—'}</strong></div>
                <div style={{ gridColumn: '1 / -1', padding: '8px 10px', background: '#fafafa', borderRadius: 6, overflowWrap: 'anywhere' }}><span style={{ color: '#888' }}>Verification hash: </span><code>{result.certificate.verification_hash || '—'}</code></div>
                <div style={{ gridColumn: '1 / -1', padding: '8px 10px', background: '#fafafa', borderRadius: 6, overflowWrap: 'anywhere' }}><span style={{ color: '#888' }}>PDF SHA-256: </span><code>{result.certificate.document_hash || '—'}</code></div>
              </div>
              <p style={{ margin: '12px 0 0', color: '#64748b', fontSize: '0.75rem', lineHeight: 1.5 }}>
                गोपनीयतेसाठी अर्जातील वैयक्तिक माहिती सार्वजनिक सत्यापन पृष्ठावर दाखवली जात नाही.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Back link */}
      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <Link to={`/gp/${tenantSlug}`} style={{ color: '#1976d2', fontSize: '0.9rem' }}>
          ← ग्रामपंचायत पोर्टलवर मागे जा
        </Link>
      </div>
    </div>
  );
}
